import { combineReducers } from 'redux';
import * as R from 'ramda';
import {
  ORGANISATION_RECEIVE_MESSAGES,
  ORGANISATION_RECEIVE_MESSAGE,
  ORGANISATION_CREATE_MESSAGE,
  ORGANISATION_UPDATE_MESSAGE
} from '../../admin/actions';
import {
  SOCIAL_RECEIVE_FEED,
  SOCIAL_RECEIVE_SCHEDULED_MESSAGES,
  SOCIAL_RECEIVE_IMPORTANT_MESSAGES,
  SOCIAL_RECEIVE_UNREAD_MESSAGES,
  SOCIAL_POST_MESSAGE,
  SOCIAL_EDIT_MESSAGE,
  SOCIAL_RECEIVE_MESSAGE,
  SOCIAL_VOTE_POLL,
} from '../actions';

const isPoll = R.filter(R.propEq('object_type', 'poll'));
const addToStore = (acc, object) => R.assoc(object.source_id, {
  ...object.source,
  options: R.pluck('id', object.source.options),
  user_id: object.user_id,
}, acc);

const items = (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_FEED:
    case SOCIAL_RECEIVE_SCHEDULED_MESSAGES:
    case SOCIAL_RECEIVE_IMPORTANT_MESSAGES:
    case SOCIAL_RECEIVE_UNREAD_MESSAGES:
    case ORGANISATION_RECEIVE_MESSAGES:
      return R.pipe(
        R.pluck('children'),
        R.flatten,
        isPoll,
        R.reduce(addToStore, state),
      )(action.messages || action.items);
    case SOCIAL_RECEIVE_MESSAGE:
    case SOCIAL_POST_MESSAGE:
    case SOCIAL_EDIT_MESSAGE:
    case ORGANISATION_RECEIVE_MESSAGE:
    case ORGANISATION_CREATE_MESSAGE:
    case ORGANISATION_UPDATE_MESSAGE:
      if (!action.message) return state;
      // When editing a message it is possible to remove a poll.
      // When a message contains no poll, we should remove
      // the poll from state (if it exists).
      if (!R.pipe(isPoll)(action.message.children)?.length) {
        const pollIdToRemove = Object.keys(state).find((pollId) => state[pollId].message_id === action.message.source_id);
        if (pollIdToRemove) {
          const { [pollIdToRemove]: deletePoll, ...newState } = state;
          return newState;
        }
      }
      return R.pipe(isPoll, R.reduce(addToStore, state))(action.message.children);
    case SOCIAL_VOTE_POLL: {
      const poll = state[action.pollId];
      if (!poll) return state;

      if (action.poll) {
        return {
          ...state,
          [action.pollId]: {
            ...poll,
            ...action.poll,
            options: action.poll.options
              ? R.pluck('id', action.poll.options)
              : poll.options,
            user_id: poll.user_id,
          },
        };
      }

      const previousOptionIds = action.previousOptionIds || [];
      const nextOptionIds = action.optionIds || [];
      const hadVote = previousOptionIds.length > 0;
      const hasVote = nextOptionIds.length > 0;
      let totalDelta = 0;
      if (!hadVote && hasVote) totalDelta = 1;
      else if (hadVote && !hasVote) totalDelta = -1;

      return {
        ...state,
        [action.pollId]: {
          ...poll,
          vote_result: nextOptionIds,
          total_vote_count: Math.max(0, (poll.total_vote_count || 0) + totalDelta),
        },
      };
    }
    default: return state;
  }
};

const addOptionToStore = (acc, option) => R.assoc(option.id, option, acc);

const options = (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_FEED:
    case SOCIAL_RECEIVE_SCHEDULED_MESSAGES:
    case SOCIAL_RECEIVE_IMPORTANT_MESSAGES:
    case SOCIAL_RECEIVE_UNREAD_MESSAGES:
    case ORGANISATION_RECEIVE_MESSAGES:
      return R.pipe(
        R.pluck('children'),
        R.flatten,
        isPoll,
        R.map(R.path(['source', 'options'])),
        R.flatten,
        R.reduce(addOptionToStore, state),
      )(action.messages || action.items);
    case SOCIAL_RECEIVE_MESSAGE:
    case SOCIAL_POST_MESSAGE:
    case SOCIAL_EDIT_MESSAGE:
    case ORGANISATION_RECEIVE_MESSAGE:
    case ORGANISATION_CREATE_MESSAGE:
    case ORGANISATION_UPDATE_MESSAGE:
      if (!action.message) return state;

      return R.pipe(
        isPoll,
        R.map(R.path(['source', 'options'])),
        R.flatten,
        R.reduce(addOptionToStore, state),
      )(action.message.children);
    case SOCIAL_VOTE_POLL: {
      if (action.poll?.options) {
        return R.reduce(addOptionToStore, state, action.poll.options);
      }

      const previousOptionIds = action.previousOptionIds || [];
      const nextOptionIds = action.optionIds || [];
      const removedOptionIds = previousOptionIds.filter((id) => !nextOptionIds.includes(id));
      const addedOptionIds = nextOptionIds.filter((id) => !previousOptionIds.includes(id));

      let nextState = state;
      removedOptionIds.forEach((id) => {
        if (!nextState[id]) return;
        nextState = {
          ...nextState,
          [id]: {
            ...nextState[id],
            vote_count: Math.max(0, (nextState[id].vote_count || 0) - 1),
          },
        };
      });
      addedOptionIds.forEach((id) => {
        if (!nextState[id]) return;
        nextState = {
          ...nextState,
          [id]: {
            ...nextState[id],
            vote_count: (nextState[id].vote_count || 0) + 1,
          },
        };
      });
      return nextState;
    }
    default: return state;
  }
};

const addToMessage = (acc, object) => R.assoc(object.parent_id, object.source_id, acc);

const messages = (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_FEED:
    case SOCIAL_RECEIVE_SCHEDULED_MESSAGES:
    case SOCIAL_RECEIVE_IMPORTANT_MESSAGES:
    case SOCIAL_RECEIVE_UNREAD_MESSAGES:
    case ORGANISATION_RECEIVE_MESSAGES:
      return R.pipe(
        R.pluck('children'),
        R.flatten,
        isPoll,
        R.reduce(addToMessage, state),
      )(action.messages || action.items);
    case SOCIAL_POST_MESSAGE:
    case SOCIAL_RECEIVE_MESSAGE:
    case SOCIAL_EDIT_MESSAGE:
    case ORGANISATION_RECEIVE_MESSAGE:
    case ORGANISATION_CREATE_MESSAGE:
    case ORGANISATION_UPDATE_MESSAGE:
      if (!action.message) return state;

      return R.pipe(isPoll, R.reduce(addToMessage, state))(action.message.children);
    default: return state;
  }
};

const findOptionById = R.curry((state, id) => state.social.polls.options[id]);

export const findByMessageId = R.curry((state, messageId) => {
  const poll = state.social.polls.items[state.social.polls.messages[messageId]];

  if (!poll) return null;

  return {
    ...poll,
    options: R.map(findOptionById(state), poll.options || []),
  };
});

export default combineReducers({ items, options, messages });
